import type { FieldConfig } from '@/pages/print/schemas';
import type { LabelRevisionData } from '@/types/types';

export const DEFAULT_DATE_FORMAT = 'yyyy-MM-dd';
export const DEFAULT_TIME_FORMAT = 'HH:mm';
type LoftwareDateTokenFn = (date: Date) => string | number;

export const normalizeRow = (row: Record<string, string>) => {
  const normalized: Record<string, string | number> = {};
  for (const key in row) {
    const value = row[key];
    if (
      typeof value === 'string' &&
      value.trim() !== '' &&
      !Number.isNaN(Number(value)) &&
      !/^0\d+/.test(value)
    ) {
      normalized[key] = value.includes('.') ? parseFloat(value) : parseInt(value, 10);
    } else {
      normalized[key] = value;
    }
  }
  return normalized;
};

export const createUniqueId = () => `id_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Loftware date format token map
const getDayOfYear = (date: Date) => {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date.getTime() - start.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
};

const getWeekOfYear = (date: Date) => {
  const firstJan = new Date(date.getFullYear(), 0, 1);
  const days = Math.floor((date.getTime() - firstJan.getTime()) / (24 * 60 * 60 * 1000));
  return Math.ceil((days + firstJan.getDay() + 1) / 7);
};

export const loftwareFormatMap: Record<string, LoftwareDateTokenFn> = {
  d: (d) => String(d.getDate()),
  dd: (d) => String(d.getDate()).padStart(2, '0'),
  M: (d) => String(d.getMonth() + 1),
  MM: (d) => String(d.getMonth() + 1).padStart(2, '0'),
  yy: (d) => String(d.getFullYear()).slice(-2),
  yyyy: (d) => String(d.getFullYear()),
  ddd: (d) => d.toLocaleString('en-US', { weekday: 'short' }),
  dddd: (d) => d.toLocaleString('en-US', { weekday: 'long' }),
  MMM: (d) => d.toLocaleString('en-US', { month: 'short' }),
  MMMM: (d) => d.toLocaleString('en-US', { month: 'long' }),
  J: (d) => String(getDayOfYear(d)),
  JJJ: (d) => String(getDayOfYear(d)).padStart(3, '0'),
  W: (d) => String(getWeekOfYear(d)),
  WW: (d) => String(getWeekOfYear(d)).padStart(2, '0'),
  N: (d) => {
    const day = d.getDay();
    return String(day === 0 ? 7 : day);
  },
  HH: (d) => String(d.getHours()).padStart(2, '0'),
  h: (d) => String(d.getHours()),
  mm: (d) => String(d.getMinutes()).padStart(2, '0'),
  ss: (d) => String(d.getSeconds()).padStart(2, '0'),
  'AM/PM': (d) => (d.getHours() < 12 ? 'AM' : 'PM'),
};

export const formatTimeWithInputFormat = (time: Date | string | number, format: string): string => {
  let d: Date;
  if (typeof time === 'string' && /^\d{2}:\d{2}$/.test(time)) {
    const [hours, minutes] = time.split(':').map(Number);
    d = new Date();
    d.setHours(hours, minutes, 0, 0);
  } else if (time instanceof Date) {
    d = time;
  } else {
    d = new Date(time);
  }

  if (Number.isNaN(d.getTime())) return '';

  const is12Hour = /AM\/PM/.test(format);

  const localFormatMap: Record<string, LoftwareDateTokenFn> = {
    ...loftwareFormatMap,
    hh: is12Hour
      ? (d: Date) => String(d.getHours() % 12 === 0 ? 12 : d.getHours() % 12).padStart(2, '0')
      : (d: Date) => String(d.getHours()).padStart(2, '0'),
  };

  const tokenRegex = /(HH|hh|h|mm|ss|AM\/PM)/g;

  return format.replace(tokenRegex, (match) => {
    const fn = localFormatMap[match];
    return fn ? String(fn(d)) : match;
  });
};

export function formatDateWithInputFormat(date: Date | string | number, format: string): string {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';

  const tokenRegex = /(dddd|ddd|yyyy|yy|MMMM|MMM|MM|dd|JJJ|J|WW|W|N|d|M)/g;

  return format.replace(tokenRegex, (match) => {
    const fn = loftwareFormatMap[match];
    return fn ? String(fn(d)) : match;
  });
}

export const areRequiredLabelFieldsFilled = (
  labelRevisionData: LabelRevisionData | null,
  variables: Record<string, string | number>[]
) => {
  if (!labelRevisionData?.fields) return true;

  return labelRevisionData.fields.every((fieldConfig: FieldConfig) => {
    return variables.every((variable) => {
      if (fieldConfig.required) {
        const value = variable[fieldConfig.name];
        return value && String(value).trim() !== '';
      }
      return true;
    });
  });
};

export const getTodayAsISO = (): string => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const isTime = (type: string): boolean => {
  return ['Time'].includes(type);
};

export const isDate = (type: string): boolean => {
  return ['Date', 'CurrentDate'].includes(type);
};

export const convertTimeTo24Hour = (timeStr: string): string => {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s?(AM|PM)$/i);
  if (!match) return timeStr;

  const [_, hour, minute, , period] = match;
  if (!period) return timeStr;
  let h = parseInt(hour, 10);
  if (period.toUpperCase() === 'PM' && h !== 12) h += 12;
  if (period.toUpperCase() === 'AM' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${minute}`;
};

export const convertDateToISO = (dateStr: string): string => {
  if (!dateStr) return '';
  let year = '',
    month = '',
    day = '';

  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    // yyyy-MM-dd
    [year, month, day] = dateStr.split('-');
  } else if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateStr)) {
    // dd/MM/yyyy
    [day, month, year] = dateStr.split('/');
  } else if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    // dd-MM-yyyy
    [day, month, year] = dateStr.split('-');
  } else if (/^\d{1,2}\/\d{1,2}\/\d{2}$/.test(dateStr)) {
    // M/D/YY or MM/DD/YY
    [month, day, year] = dateStr.split('/');
    year = `20${year.padStart(2, '0')}`; // Assumes 2000+ for 2-digit years
    month = month.padStart(2, '0');
    day = day.padStart(2, '0');
  } else if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateStr)) {
    // M/D/YYYY or MM/DD/YYYY
    [month, day, year] = dateStr.split('/');
    month = month.padStart(2, '0');
    day = day.padStart(2, '0');
  } else {
    return dateStr;
  }

  return `${year}-${month}-${day}`;
};
